import React, { useMemo } from "react";
import { Loader } from "components/Loader";
import { TableSection } from "features/Research/v2/components/StockDetailPage/StockDetailPage.styles";
import { Text } from "style/v2/components/Typography";
import {
  Card,
  CardHeader,
  CardContent,
  TitleInfoIcon,
  TooltipText,
} from "../TopHoldings/TopHoldings.styles";
import { columns } from "./columns";
import PropTypes from "prop-types";
import { getStockCompanyIcon } from "utils/index";
import { Table } from "components/Table";
import { Tooltip } from "components/Tooltip";

const TOP_HOLDINGS_ALLOCATIONS_TOOLTIP = (
  <>
    <TooltipText>30 Day: Average weight over the last 30 days</TooltipText>
    <TooltipText>Average: Average weight since inception</TooltipText>
    <TooltipText>
      Current: Weight today, matching Top Holdings above
    </TooltipText>
    <TooltipText>
      &quot;--&quot;: Holding wasn&apos;t in the portfolio during that window
    </TooltipText>
  </>
);

const toMap = (arr) =>
  Object.fromEntries((arr || []).map(({ name, value }) => [name, value]));

const TopHoldingsDetail = ({
  holdings30d,
  holdingsAverage,
  holdingsCurrent,
  isLoading,
}) => {
  const { holdings, hasData } = useMemo(() => {
    const map30d = toMap(holdings30d);
    const mapAverage = toMap(holdingsAverage);
    const mapCurrent = toMap(holdingsCurrent);

    const allNames = [
      ...new Set([
        ...Object.keys(map30d),
        ...Object.keys(mapAverage),
        ...Object.keys(mapCurrent),
      ]),
    ];

    const result = allNames.map((name) => ({
      id: name,
      asset: {
        symbol: name,
        imageUrl: getStockCompanyIcon(name),
      },
      holdings30d: map30d[name] ?? null,
      holdingsAverage: mapAverage[name] ?? null,
      holdingsCurrent: mapCurrent[name] ?? null,
    }));

    // Current allocation first, since that's what the strategy actually
    // holds today; 30 Day weight breaks ties for holdings with no current
    // position (e.g. sold since).
    result.sort((a, b) => {
      const current =
        (b.holdingsCurrent ?? -Infinity) - (a.holdingsCurrent ?? -Infinity);
      if (current !== 0) return current;
      return (b.holdings30d ?? -Infinity) - (a.holdings30d ?? -Infinity);
    });

    return {
      holdings: result,
      hasData: result.some(
        (h) =>
          h.holdings30d != null ||
          h.holdingsAverage != null ||
          h.holdingsCurrent != null
      ),
    };
  }, [holdings30d, holdingsAverage, holdingsCurrent]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Loader />
        </CardContent>
      </Card>
    );
  }

  if (!hasData) return null;

  return (
    <TableSection>
      <Card>
        <CardHeader>
          <Text variant="cardHeading" weight="500" as="h3">
            Top Holdings Allocations
            <Tooltip
              value={TOP_HOLDINGS_ALLOCATIONS_TOOLTIP}
              placement="top"
              tooltipClassName="v2-tooltip"
            >
              <TitleInfoIcon size={14} />
            </Tooltip>
          </Text>
        </CardHeader>
        <CardContent>
          <Table
            data={holdings}
            columns={columns}
            isLoading={isLoading}
            notFoundText="No holdings data available"
            showPagination={false}
          />
        </CardContent>
      </Card>
    </TableSection>
  );
};

TopHoldingsDetail.propTypes = {
  holdings30d: PropTypes.array,
  holdingsAverage: PropTypes.array,
  holdingsCurrent: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default TopHoldingsDetail;
